import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { ContentCopy, Delete } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import useHandleConnected from "./useHandleConnected";

// Injeta keyframes do highlight (uma unica vez)
if (typeof document !== "undefined" && !document.getElementById("node-highlight-keyframes")) {
  const style = document.createElement("style");
  style.id = "node-highlight-keyframes";
  style.textContent = `
    @keyframes nodeHighlightPulse {
      0%, 100% { box-shadow: 0 0 0 4px rgba(245,158,11,0.35), 0 0 20px rgba(245,158,11,0.25); }
      50% { box-shadow: 0 0 0 8px rgba(245,158,11,0.15), 0 0 30px rgba(245,158,11,0.4); }
    }
    .n8n-node-actions {
      opacity: 0;
      transition: opacity 0.15s;
    }
    .n8n-node:hover .n8n-node-actions {
      opacity: 1;
    }
    .n8n-node .react-flow__handle {
      width: 12px !important;
      height: 12px !important;
      background: #ef4444 !important;
      border: 2.5px solid #fff !important;
      box-shadow: 0 0 0 1px #fca5a5;
      transition: transform 0.15s, background 0.15s, box-shadow 0.15s;
    }
    .n8n-node .react-flow__handle.handle-connected {
      background: #22c55e !important;
      box-shadow: 0 0 0 1px #86efac;
    }
    .n8n-node .react-flow__handle-left {
      left: -7px !important;
    }
    .n8n-node .react-flow__handle-right {
      right: -7px !important;
    }
    .n8n-node:hover .react-flow__handle {
      box-shadow: 0 0 0 2px rgba(239,68,68,0.3);
    }
    .n8n-node:hover .react-flow__handle.handle-connected {
      box-shadow: 0 0 0 2px rgba(34,197,94,0.3);
    }
    .n8n-node .react-flow__handle:hover {
      transform: scale(1.3);
      box-shadow: 0 0 0 3px rgba(99,102,241,0.4);
      background: #6366f1 !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * NodeWrapper V2 — Estilo n8n
 *
 * Layout: icone quadrado colorido a esquerda | titulo + conteudo a direita
 * Handles: discretos, aparecem on hover
 */
const NodeWrapper = memo(({
  id,
  icon,
  title,
  subtitle,
  color = "#9a00ed",
  bgColor = "#fff",
  borderColor,
  children,
  handles = [],
  noTarget = false,
  noSource = false,
  isConnectable = true,
  minWidth = 200,
  selected = false,
  targetHandleId,
  data,
}) => {
  const storageItems = useNodeStorage();
  const getHandleClass = useHandleConnected(id);

  // Sobrescritas de aparencia pelo Inspector
  const effectiveBgColor = data?.bgColor || bgColor;
  const effectiveTitle = data?.labelOverride || title;
  const isHighlighted = data?._highlight === true;
  const simStatus = data?._simStatus;

  // Cores por estado
  const SIM_COLORS = { current: "#f59e0b", executed: "#22c55e", error: "#ef4444" };

  const borderFinal = simStatus
    ? SIM_COLORS[simStatus]
    : isHighlighted
      ? "#f59e0b"
      : selected
        ? color
        : "#e5e7eb";

  const boxShadow = simStatus === "current"
    ? "0 0 0 4px rgba(245,158,11,0.35), 0 0 20px rgba(245,158,11,0.25)"
    : simStatus === "executed"
      ? "0 0 0 3px rgba(34,197,94,0.25)"
      : simStatus === "error"
        ? "0 0 0 3px rgba(239,68,68,0.3)"
        : isHighlighted
          ? "0 0 0 4px rgba(245,158,11,0.35), 0 0 20px rgba(245,158,11,0.25)"
          : selected
            ? `0 0 0 2px ${color}30, 0 4px 12px rgba(0,0,0,0.08)`
            : "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)";

  // Handle style (visual controlado via CSS .react-flow__handle)
  const handleBase = {
    cursor: "crosshair",
  };

  return (
    <div
      className="n8n-node"
      style={{
        backgroundColor: effectiveBgColor,
        borderRadius: 8,
        border: `1.5px solid ${borderFinal}`,
        boxShadow,
        minWidth,
        maxWidth: 320,
        position: "relative",
        transition: "box-shadow 0.2s, border-color 0.2s",
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        animation: (isHighlighted || simStatus === "current") ? "nodeHighlightPulse 0.6s ease-in-out 3" : "none",
        display: "flex",
        flexDirection: "row",
        overflow: "visible",
      }}
    >
      {/* Handle de entrada */}
      {!noTarget && (
        <Handle
          type="target"
          position={Position.Left}
          id={targetHandleId || undefined}
          className={getHandleClass("target", targetHandleId)}
          style={{
            ...handleBase,
            left: -7,
          }}
          isConnectable={isConnectable}
        />
      )}

      {/* Icone lateral estilo n8n */}
      <div style={{
        width: 44,
        minHeight: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: color,
        flexShrink: 0,
        borderRadius: "7px 0 0 7px",
      }}>
        {icon && React.cloneElement(icon, {
          style: { fontSize: 22, color: "#fff", ...icon.props?.style },
        })}
      </div>

      {/* Conteudo */}
      <div style={{
        flex: 1,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
      }}>
        {/* Header: titulo + acoes */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}>
          <span style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "#1a1a1a",
            flex: 1,
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {effectiveTitle}
          </span>

          {/* Acoes (duplicar/deletar) — aparecem on hover */}
          <span className="n8n-node-actions" style={{
            display: "flex",
            gap: 2,
            flexShrink: 0,
          }}>
            <ContentCopy
              onClick={(e) => {
                e.stopPropagation();
                storageItems.setNodesStorage(id);
                storageItems.setAct("duplicate");
              }}
              style={{ fontSize: 12, color: "#9ca3af", cursor: "pointer" }}
            />
            <Delete
              onClick={(e) => {
                e.stopPropagation();
                storageItems.setNodesStorage(id);
                storageItems.setAct("delete");
              }}
              style={{ fontSize: 12, color: "#9ca3af", cursor: "pointer" }}
            />
          </span>
        </div>

        {/* Subtitulo */}
        {subtitle && (
          <div style={{
            fontSize: 11,
            color: "#6b7280",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}>
            {subtitle}
          </div>
        )}

        {/* Conteudo do node */}
        {children && (
          <div style={{ marginTop: 2 }}>
            {children}
          </div>
        )}
      </div>

      {/* Handle de saida padrao */}
      {!noSource && handles.length === 0 && (
        <Handle
          type="source"
          position={Position.Right}
          id="a"
          className={getHandleClass("source", "a")}
          style={{
            ...handleBase,
            right: -7,
          }}
          isConnectable={isConnectable}
        />
      )}

      {/* Handles customizados */}
      {handles.map((h) => (
        <Handle
          key={h.id}
          type={h.type || "source"}
          position={h.position || Position.Right}
          id={h.id}
          className={getHandleClass(h.type || "source", h.id)}
          style={{
            ...handleBase,
            ...(h.style || {}),
          }}
          isConnectable={isConnectable}
        />
      ))}
    </div>
  );
});

NodeWrapper.displayName = "NodeWrapper";
export default NodeWrapper;
